import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
// import { AxiosError } from 'axios'; // Not directly used, errors are cast to any or firebase specific types
import { useDashboardStore } from '@/features/dashboard/stores/dashboard.store'; // Import useDashboardStore
import * as Sentry from '@sentry/react';
import { devtools } from 'zustand/middleware';
import { resetAllStores } from '@/features/auth/utils/store-resets.utils';

// Firebase imports
import {
  User as FirebaseUser, // Rename to avoid conflict with our User type if any
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  getIdToken,
  type ActionCodeSettings, // Import ActionCodeSettings type
} from 'firebase/auth';
import { auth as firebaseAuth } from '@/features/auth/config/firebase-config'; // Import Firebase auth instance
// Removed: import { trackError } from '@/features/help/utils/trackError';
// Removed: import { API_CLIENT } from '@/features/api/axios';
// Removed: import { UserTheme } from '@/features/settings/stores/themeStore';

// Helper function to get the base URL from environment variables
const getAppBaseUrl = () => {
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  if (!baseUrl) {
    logger.warn(
      'AuthStore',
      'VITE_APP_BASE_URL is not defined. Falling back to window.location.origin for action URL.',
      {
        origin: window.location.origin,
      }
    );
    // Fallback to window.location.origin if VITE_APP_BASE_URL is not set,
    // but log a warning as this should ideally be configured.
    return window.location.origin;
  }
  return baseUrl;
};

// For storing app-specific user details fetched from our backend
export interface AppUser {
  userId: number; // This is the postgresql user id
  firebaseUid: string;
  userEmail: string;
  userName: string | null;
  companyName: string | null;
  viewed_settings?: boolean;
  shortId: string | null; // User's short ID for URL generation
  crmProvider: string | null; // Selected CRM provider (e.g., 'salesforce', 'hubspot')
  // Removed: theme?: UserTheme;
}

interface AuthState {
  firebaseUser: FirebaseUser | null;
  appUser: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isAppInitializing: boolean; // Tracks if app-specific user data is being fetched
  isRegistering: boolean; // Add this to track registration process
  login: (email: string, password: string) => Promise<FirebaseUser | null>;
  register: (
    name: string,
    companyName: string,
    email: string,
    password: string
  ) => Promise<FirebaseUser | null>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  getFirebaseIdToken: () => Promise<string | null>;
  resendVerificationEmail: () => Promise<boolean>;
  setAppUser: (user: AppUser | null) => void; // Added to allow external updates if needed
  setIsAppInitializing: (initializing: boolean) => void;
  clearAuthError: () => void; // Added to clear authentication errors
  isCrmConnected: boolean;
  selectedProvider: string | null; // Track the selected CRM provider
  showProviderSelection: boolean; // Control provider selection modal
  initiateOAuth: (provider: string) => Promise<void>;
  checkCrmConnection: () => Promise<boolean>;
  handleCrmCallback: (provider: string, code: string) => Promise<void>;
  setSelectedProvider: (provider: string | null) => void;
  setShowProviderSelection: (show: boolean) => void;
}

// Define a specific error message or code for re-authentication
export const CRM_REAUTH_REQUIRED_SIGNAL = 'CRM_REAUTH_REQUIRED';

// We can consider using persist middleware if we want to persist parts of authUser (like appUser) to localStorage
// For now, Firebase handles its own persistence for firebaseUser.
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => {
        /**
         * Fetches app-specific user data from the backend /api/auth/me endpoint.
         * This is the single source of truth for the appUser object.
         */
        const fetchAppUser = async (user: FirebaseUser) => {
          const currentPath = window.location.pathname;
          if (currentPath.startsWith('/application/')) {
            logger.info(
              'AuthStore',
              '[fetchAppUser] User is on a public application page. Skipping /api/auth/me call.',
              { uid: user.uid, currentPath }
            );
            return;
          }

          logger.info(
            'AuthStore',
            '[fetchAppUser] Attempting to fetch user details from /api/auth/me.',
            {
              uid: user.uid,
            }
          );

          try {
            const idToken = await getIdToken(user, true);
            const response = await api.get('/api/auth/me', {
              headers: {
                Authorization: `Bearer ${idToken}`,
              },
            });

            const backendUser = response.data;
            const appUserDetails: AppUser = {
              userId: backendUser.id,
              firebaseUid: user.uid,
              userEmail: backendUser.email,
              userName: backendUser.name,
              companyName: backendUser.company_name,
              viewed_settings: backendUser.viewed_settings,
              shortId: backendUser.short_id || null,
              crmProvider: backendUser.crm_provider || null,
            };

            set({ appUser: appUserDetails });
            logger.info('AuthStore', '[fetchAppUser] Successfully fetched and set appUser.', {
              appUserDetails,
            });

            // Initialize other stores that depend on appUser
            if (typeof appUserDetails.viewed_settings === 'boolean') {
              useDashboardStore.getState().initializeSettingsStatus(appUserDetails.viewed_settings);
            } else {
              useDashboardStore.getState().initializeSettingsStatus(false);
            }
          } catch (error: any) {
            logger.error('AuthStore', '[fetchAppUser] Failed to fetch app-specific user details.', {
              uid: user.uid,
              error: error.response?.data || error.message,
            });
            set({
              appUser: null,
              error: 'Failed to load your profile data. Some features may be unavailable.',
            });
          }
        };

        /**
         * Handles the logic for a user signing in.
         */
        const handleUserSignedIn = async (user: FirebaseUser) => {
          logger.info('AuthStore', '[handleUserSignedIn] User signed in.', {
            uid: user.uid,
            email: user.email,
          });
          Sentry.setUser({
            id: user.uid,
            email: user.email || undefined,
            username: user.displayName || undefined,
          });

          // If a registration is in progress, only set the core user details.
          // The `register` function is responsible for the loading state and fetching the app user profile.
          if (get().isRegistering) {
            logger.info(
              'AuthStore',
              '[handleUserSignedIn] Registration in progress, deferring app user fetch.'
            );
            set({ firebaseUser: user, isAuthenticated: true, error: null });
            return;
          }

          // For normal logins, proceed with the full sign-in flow.
          set({ firebaseUser: user, isAuthenticated: true, isLoading: true, error: null });
          await fetchAppUser(user);
          set({ isLoading: false });
        };

        /**
         * Handles the logic for a user signing out.
         */
        const handleUserSignedOut = () => {
          logger.info('AuthStore', '[handleUserSignedOut] User signed out.');
          Sentry.setUser(null);
          set({
            firebaseUser: null,
            appUser: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            isAppInitializing: false,
            isCrmConnected: false,
            isRegistering: false, // Initialize the flag
            selectedProvider: null,
            showProviderSelection: false,
          });
          resetAllStores();
        };

        // Core listener for Firebase auth state changes.
        onAuthStateChanged(firebaseAuth, async (user) => {
          if (user) {
            await handleUserSignedIn(user);
          } else {
            handleUserSignedOut();
          }
        });

        return {
          firebaseUser: null,
          appUser: null,
          isLoading: true, // Start loading until first auth check completes.
          error: null,
          isAuthenticated: false,
          isAppInitializing: false,
          isCrmConnected: false,
          isRegistering: false, // Initialize the flag
          selectedProvider: null,
          showProviderSelection: false,

          setAppUser: (user) => set({ appUser: user }),
          setIsAppInitializing: (initializing) => set({ isAppInitializing: initializing }),
          clearAuthError: () => set({ error: null }),
          setSelectedProvider: (provider) => set({ selectedProvider: provider }),
          setShowProviderSelection: (show) => set({ showProviderSelection: show }),

          getFirebaseIdToken: async () => {
            const user = get().firebaseUser;
            if (user) {
              try {
                return await getIdToken(user);
              } catch (error: any) {
                logger.error('AuthStore', 'Error getting Firebase ID token', { error });
                if (
                  error.code === 'auth/user-token-expired' ||
                  error.code === 'auth/invalid-user-token'
                ) {
                  await get().logout();
                }
                return null;
              }
            }
            return null;
          },

          logout: async () => {
            try {
              await signOut(firebaseAuth);
              logger.info('AuthStore', 'User explicitly logged out via logout function.');
              // onAuthStateChanged will handle the rest.
            } catch (error) {
              logger.error('AuthStore', 'Error during logout', { error });
              Sentry.captureException(error);
              handleUserSignedOut(); // Manually trigger sign-out flow on error.
            }
          },

          login: async (email, password) => {
            set({ isLoading: true, error: null });
            try {
              const userCredential = await signInWithEmailAndPassword(
                firebaseAuth,
                email,
                password
              );
              // onAuthStateChanged will handle the user state update.
              set({ isLoading: false });
              return userCredential.user;
            } catch (error: any) {
              logger.error('AuthStore', 'Login failed', {
                error: error,
                errorCode: error.code,
                fullError: error,
              });
              const UImessage =
                error.code === 'auth/user-not-found' ||
                error.code === 'auth/wrong-password' ||
                error.code === 'auth/invalid-credential'
                  ? 'Invalid credentials. Please try again.'
                  : 'An unexpected error occurred during login.';
              set({ error: UImessage, isAuthenticated: false, isLoading: false });
              throw error;
            }
          },

          register: async (name, companyName, email, password) => {
            set({ isLoading: true, error: null, isRegistering: true }); // Set flag
            try {
              const userCredential = await createUserWithEmailAndPassword(
                firebaseAuth,
                email,
                password
              );
              const firebaseUser = userCredential.user;
              logger.info('AuthStore', 'User created in Firebase', { uid: firebaseUser.uid });

              // Send verification email
              try {
                const appBaseUrl = getAppBaseUrl();
                const actionCodeSettings: ActionCodeSettings = {
                  url: `${appBaseUrl}/auth/action`,
                  handleCodeInApp: true,
                };
                await sendEmailVerification(firebaseUser, actionCodeSettings);
              } catch (verificationError: any) {
                logger.error('AuthStore', 'Failed to send verification email', {
                  error: verificationError,
                });
                // It's crucial to re-throw or handle this error visibly to the user.
                // For now, we'll re-throw to let the caller handle UI feedback.
                throw verificationError;
              }

              const idToken = await getIdToken(firebaseUser, true);

              // Create user profile in backend
              const backendResponse = await api.post(
                '/api/auth/register',
                { name, companyName },
                { headers: { Authorization: `Bearer ${idToken}` } }
              );

              const backendRegisteredUser = backendResponse.data;
              const appUserDetails: AppUser = {
                userId: backendRegisteredUser.id,
                firebaseUid: firebaseUser.uid,
                userEmail: backendRegisteredUser.email,
                userName: backendRegisteredUser.name,
                companyName: backendRegisteredUser.company_name,
                viewed_settings: backendRegisteredUser.viewed_settings,
                shortId: backendRegisteredUser.short_id || null,
                crmProvider: backendRegisteredUser.crm_provider || null,
              };

              // Directly set the app user here to ensure it's available immediately after registration
              set({ appUser: appUserDetails, isLoading: false, isRegistering: false });
              // The onAuthStateChanged will also fire, but it will fetch the same user data, which is fine.

              logger.info('AuthStore', 'Backend registration successful', { appUserDetails });

              return firebaseUser;
            } catch (error: any) {
              logger.error('AuthStore', 'Registration failed', { error });
              let UImessage = 'An unexpected error occurred during registration.';
              if (error.code === 'auth/email-already-in-use') {
                UImessage = 'This email is already in use. Please login or use a different email.';
              } else if (error.code === 'auth/weak-password') {
                UImessage = 'The password is too weak. Please choose a stronger password.';
              } else if (error.code === 'auth/invalid-email') {
                UImessage = 'The email address is not valid.';
              } else if (error.response?.status === 409) {
                UImessage =
                  error.response.data.error || 'This email is already in use by an account.';
              } else if (error.message.includes('verification')) {
                UImessage =
                  'Your account was created, but we failed to send a verification email. Please try the "resend" option.';
              }
              set({ error: UImessage, isLoading: false, isRegistering: false }); // Reset flag on error
              throw error;
            }
          },

          resendVerificationEmail: async () => {
            const user = get().firebaseUser;
            if (!user) {
              logger.warn('AuthStore', 'resendVerificationEmail called without a user.');
              return false;
            }
            if (user.emailVerified) {
              logger.info('AuthStore', 'User email is already verified.');
              return true;
            }
            try {
              await sendEmailVerification(user);
              logger.info('AuthStore', 'Verification email resent.', { uid: user.uid });
              return true;
            } catch (error: any) {
              logger.error('AuthStore', 'Failed to resend verification email', { error });
              return false;
            }
          },

          requestPasswordReset: async (email: string) => {
            try {
              await sendPasswordResetEmail(firebaseAuth, email);
              logger.info('AuthStore', 'Password reset email sent.', { email });
            } catch (error: any) {
              logger.error('AuthStore', 'Failed to send password reset email', { error });
              throw error;
            }
          },

          // CRM-related functions remain unchanged for now
          initiateOAuth: async (provider) => {
            try {
              // Set the selected provider before redirecting so it's available when we return from OAuth
              set({ selectedProvider: provider });

              const response = await api.get(`/api/crm/authorize`, { params: { provider } });
              if (response.data.authUrl) {
                window.location.href = response.data.authUrl;
              }
            } catch (error: any) {
              logger.error('AuthStore', `Error initiating ${provider} OAuth`, { error });
              set({ error: `Failed to start ${provider} connection process.` });
              throw error;
            }
          },

          checkCrmConnection: async () => {
            try {
              const response = await api.get('/api/crm/connection-status');
              const isConnected = response.data.isConnected;
              set({ isCrmConnected: isConnected, error: null });
              return isConnected;
            } catch (error: any) {
              logger.error('AuthStore', 'Error checking CRM connection', { error });
              set({ isCrmConnected: false });
              return false;
            }
          },

          handleCrmCallback: async (provider, code) => {
            try {
              await api.post('/api/crm/callback', { provider, code });
              set({ isCrmConnected: true, error: null });

              // Refresh user profile to get updated CRM provider information
              const currentUser = get().firebaseUser;
              if (currentUser) {
                logger.info('AuthStore', 'Refreshing user profile after CRM connection', {
                  provider,
                });
                await fetchAppUser(currentUser);
              }
            } catch (error: any) {
              logger.error('AuthStore', `Error handling ${provider} OAuth callback`, { error });
              set({ error: `Failed to finalize ${provider} connection.` });
              throw error;
            }
          },
        };
      },
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({ selectedProvider: state.selectedProvider }),
      }
    )
  )
);
